import React from 'react';
import { Box, Skeleton, Stack } from '@mui/material';
import { tokens } from '../theme';

/**
 * Shown while a proposal chunk and its data load.
 *
 * This is a content-shaped skeleton rather than a spinner: it occupies the same
 * geometry the real cover will, so the layout does not jump when the proposal
 * arrives (which keeps Cumulative Layout Shift near zero) and the wait reads as
 * progress instead of a blank screen.
 */
export default function ProposalSkeleton() {
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        minHeight: '100vh',
        '@supports (height: 100dvh)': { minHeight: '100dvh' },
        bgcolor: 'background.default',
        px: { xs: 2, md: 6 },
        py: { xs: 3, md: 6 }
      }}
    >
      <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
        {/* Brand bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: { xs: 3, md: 5 } }}
        >
          <Skeleton variant="rounded" width={150} height={34} />
          <Skeleton variant="circular" width={44} height={44} />
        </Stack>

        {/* Cover block */}
        <Skeleton
          variant="rounded"
          sx={{
            height: { xs: 220, sm: 300, md: 420 },
            borderRadius: tokens.radius['2xl'],
            mb: { xs: 3, md: 5 }
          }}
        />

        {/* Headline + intro copy */}
        <Stack spacing={1.5} sx={{ mb: { xs: 4, md: 6 }, maxWidth: 720 }}>
          <Skeleton variant="text" width="72%" sx={{ fontSize: tokens.fontSize['3xl'] }} />
          <Skeleton variant="text" width="94%" />
          <Skeleton variant="text" width="88%" />
          <Skeleton variant="text" width="61%" />
        </Stack>

        {/* Pricing cards — the block clients scroll to first */}
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }
          }}
        >
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              sx={{ height: { xs: 180, md: 300 }, borderRadius: tokens.radius['2xl'] }}
            />
          ))}
        </Box>
      </Box>

      {/* Note: sizes must be strings here — MUI's sx treats a bare `width: 1`
          as 100%, which made this element 375px wide and pushed the page into
          horizontal scroll. */}
      <Box
        component="span"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0
        }}
      >
        Caricamento del preventivo in corso…
      </Box>
    </Box>
  );
}
